import React from 'react';
import { PersonType } from '@marageti/z4-sdk/lib/people';
import {
  Message, MessageType, Partner, TripMatch, TripRequest, TripRequestType,
} from '@marageti/z4-sdk/lib/travel';
import { useApiClient, useFetch } from '@marageti/z4-lib';
import { useSnackbar } from 'notistack';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuthStore } from '../../../../store';
import useZicassoChat from '../../../../hooks/use-zicasso-chat';
import ConversationView from './conversation-view';
import useDocumentDownload from '../../../../hooks/use-document-download';

type Props = {
  conversationId?: string | undefined,
  conversationTitle?: string,
  tripMatch: TripMatch | undefined,
};

const ConversationContainer = ({
  conversationId = undefined,
  conversationTitle = undefined,
  tripMatch = undefined,
}: Props) => {
  const { loggedInTraveler } = useAuthStore();
  const apiClient = useApiClient();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { state } = useLocation();
  const { tripMatchId } = useParams();

  const [onClickDocument] = useDocumentDownload();
  const {
    isError,
    isLoading,
    isValidating,
    conversation,
    messages,
    lastSeenMessageId,
    participants,
    sendMessage,
    updateMessages,
    loadMoreMessages,
    hasMoreMessages,
  } = useZicassoChat({
    conversationId,
    viewerId: loggedInTraveler?.id,
  });
  const viewer = {
    id: loggedInTraveler?.id,
    type: PersonType.Traveler,
  };

  const localMatch = state?.data?.match;
  const { data: loadedMatch } = useFetch<TripMatch>(apiClient.tripMatchesClient.getTripMatch, [tripMatchId], !!tripMatchId);
  const match = localMatch || loadedMatch;
  const { data: tripRequest } = useFetch<TripRequest>(apiClient.tripRequestsClient.getTripRequest, [match?.tripRequestId], !!match?.tripRequestId);
  const { data: partner } = useFetch<Partner>(apiClient.partnersClient.getPartner, [match?.partnerId], !!match?.partnerId);

  const handleOnSend = (messageText: string) => {
    sendMessage({
      conversationId,
      text: messageText,
      messageType: MessageType.Text,
    });
  };

  const handleDocumentClick = async (message: Message) => {
    const documentId = message.entityId;
    try {
      if (documentId) {
        const document = await apiClient.documentsClient.getDocument(documentId);
        if (document.sourceURI) {
          window.open(document.sourceURI, '_blank');
        } else {
          onClickDocument(document);
        }
      }
    } catch (error: any) {
      const snackbarMessage = error?.code === 404 ? 'Document not found' : 'Error downloading document';
      enqueueSnackbar(snackbarMessage, { variant: 'default' });
    }
  };

  const handleTripMatchReviewRequestClick = async (message: Message) => {
    if (message.entityId) {
      navigate(`/trip-matches/${message.entityId}/review`);
    }
  };

  const handleViewClick = (message: Message) => {
    switch (message.entityType) {
      case 'Document':
        handleDocumentClick(message);
        break;
      case 'TripMatchReviewRequest':
        handleTripMatchReviewRequestClick(message);
        break;
      case 'SalesRecording':
        break;
      default:
        break;
    }
  };

  return (
    <ConversationView
      conversation={conversation}
      conversationTitle={conversationTitle}
      handleViewClick={handleViewClick}
      isError={isError}
      isLoading={isLoading}
      isValidating={isValidating}
      messages={messages}
      lastMessageSeenId={lastSeenMessageId}
      loadMoreMessages={loadMoreMessages}
      hasMoreMessages={hasMoreMessages}
      participants={participants}
      sendMessage={handleOnSend}
      viewer={viewer}
      updateMessages={updateMessages}
      tripMatch={tripMatch}
      partner={partner}
      isLegacyTR={tripRequest?.tripRequestType === TripRequestType.Legacy}
    />
  );
};

export default ConversationContainer;
